import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { usePdfSetters } from '@/hooks/pdf/usePdfSetters'
import { useErrorHandler } from '@/hooks/utility/useErrorHandler'
import { useSetToast } from '@/hooks/utility/useSetToast'
import { EventCategory, logger } from '@/utils/logger'
import { loggerSentry } from '@/utils/loggerSentry'
import {
  STATUS_KEY,
  PDF_STATUS_KEY,
  PDF_PROCESSOR_TYPE,
  FONT_LOAD_FAILED_REASON,
  PDF_GENERATION_TIMEOUT_MS,
} from '@messenger/consts'
import { shouldRenderProofAsPdf } from '@messenger/utils'
import { textGeometryDigest } from '@/utils/editor/textGeometryProbe'
import { useIsAdmin } from '@/hooks/utility/useIsAdmin'
import { useRecoilValue } from 'recoil'
import { roleAtom } from '@/atoms/globalAtoms'
import type { ApplicationType, StatusKey } from '@messenger/types'
import type { AxiosError } from 'axios'
import type { UpdatePdfStateFunction } from '@/hooks/pdf/usePdfViewer'
import {
  generatePDF as generatePDFApi,
  generatePDFStatus,
  pollScreenshotProofStatus,
  ScreenshotJobFailedError,
} from '@/api/orders'

/**
 * Whether a PDF request failed because a font on the page did not load. The
 * fetch client throws the status route's `message`, the failed job's reason,
 * as a string, and a font failure's reason carries FONT_LOAD_FAILED_REASON
 * after whatever prefix the job gave it.
 */
const isFontLoadFailure = (error: unknown): boolean => {
  const reason =
    typeof error === 'string'
      ? error
      : error instanceof Error
        ? error.message
        : ''
  return reason.includes(FONT_LOAD_FAILED_REASON)
}

/** How often the editor asks for a PDF job's status. */
const PDF_STATUS_POLL_MS = 2000

/** How many times it asks: past the worker's limit for a job, by a 30 s margin. */
const PDF_STATUS_POLL_ATTEMPTS = Math.ceil(
  (PDF_GENERATION_TIMEOUT_MS + 30_000) / PDF_STATUS_POLL_MS,
)

type UsePdfGenerationParams = {
  order: {
    id: number
    case_id?: string
    funeral_home_id: number
  }
  pageSet: {
    id: number
  }
  productInfo: {
    id: number
    status?: string
  }
  setProductStatus: (status: StatusKey) => Promise<void>
  updatePdfState: UpdatePdfStateFunction
  cleanupPdfGeneration: () => void
  applicationType: string
}

export const usePdfGeneration = ({
  order,
  pageSet,
  productInfo,
  setProductStatus,
  updatePdfState,
  cleanupPdfGeneration,
  applicationType,
}: UsePdfGenerationParams) => {
  const { t } = useTranslation()
  const handleError = useErrorHandler('usePdfGeneration')
  const setToast = useSetToast()

  const isAdmin = useIsAdmin()
  const userRole = useRecoilValue(roleAtom)
  const isFHDirector = userRole === 'fh-user'

  const {
    setGenerationState,
    setGenerating,
    setShowPdfViewer,
    setIsProof,
    setGeneratingText,
    setDisableCustomizeProductHeaderDropdowns,
    setCanCustomize,
    setCanRevertFinalization,
    setProofGalleryDetails,
    // delay setting product status for later, to avoid premature component re-renders
    setShouldSetProductStatusToFinalized,
    setShouldSetProductStatusToEditing,
    setShouldSetProductStatusToProofSent,
  } = usePdfSetters({ updatePdfState })

  const checkPDFStatus = useCallback(
    // A failed PDF ends the loop already (progress -1 → HTTP 500 →
    // generatePDFStatus throws). The ceiling outlasts the worker's own limit
    // for a job, so a failure the worker records at that limit, a named font
    // failure included, reaches the person; a worker that dies without
    // writing an end still ends the poll instead of hanging the viewer.
    async (id: string, maxAttempts = PDF_STATUS_POLL_ATTEMPTS) => {
      let attempts = 0
      let PDFstatus = await generatePDFStatus(id)
      while (
        'progress' in PDFstatus &&
        PDFstatus.progress !== 100 &&
        PDFstatus.progress !== undefined
      ) {
        if (++attempts >= maxAttempts) {
          throw new Error(t('pdf.generationTimedOut'))
        }
        await new Promise((resolve) => setTimeout(resolve, PDF_STATUS_POLL_MS))
        setGenerationState({
          progress: PDFstatus.progress,
          status: PDF_STATUS_KEY.GENERATING,
          pdf: '',
        })
        PDFstatus = await generatePDFStatus(id)
      }
      setGenerationState({
        ...PDFstatus,
        pdf: 'pdf' in PDFstatus ? (PDFstatus.pdf ?? undefined) : undefined,
      })
      return PDFstatus
    },
    [setGenerationState, t],
  )

  const pollScreenshotStatus = useCallback(
    async (pdfId: string) => {
      const status = await pollScreenshotProofStatus(pdfId)
      if (status === null) return []
      setProofGalleryDetails({
        pdfId,
        galleryToken: status.galleryToken ?? null,
        screenshotUrls: status.screenshotUrls,
      })
      return status.screenshotUrls
    },
    [setProofGalleryDetails],
  )

  const requestTextGeometry = () => {
    try {
      const digest = textGeometryDigest()
      return digest.boxes.length > 0 ? digest : undefined
    } catch (error) {
      console.warn('Text geometry digest not taken for the render request', {
        error,
      })
      return undefined
    }
  }

  const generatePDF = useCallback(
    async (
      isProof: boolean,
      selectedLayout: {
        paper_height: number
        paper_width: number
      },
    ) => {
      setGenerationState({
        status: PDF_STATUS_KEY.IN_QUEUE,
        progress: 0,
        pdf: '',
      })
      setGenerating(true)
      const shouldShowPdfViewer = isProof || isAdmin
      // Banner-sized proofs render as a PDF (the backend re-routes them through
      // the PDF pipeline because screenshots can't reliably capture a tall
      // banner). Poll PDF status and show the iframe instead of the gallery.
      const useBannerPdfProof =
        isProof && shouldRenderProofAsPdf(selectedLayout.paper_height)
      setShowPdfViewer(shouldShowPdfViewer)
      setIsProof(isProof)
      setGeneratingText(
        isProof ? t('pdf.generatingProof') : t('pdf.finalizing'),
      )
      setDisableCustomizeProductHeaderDropdowns(true)
      if (isProof) {
        setProofGalleryDetails(null)
      }

      const hasValidLayoutDimensions =
        selectedLayout &&
        selectedLayout.paper_height &&
        selectedLayout.paper_width
      const hasValidPageSet = pageSet && pageSet.id

      if (!hasValidLayoutDimensions) {
        const errorMessage = 'Invalid layout dimensions for PDF generation'
        console.error(errorMessage, { selectedLayout })
        cleanupPdfGeneration()
        setToast({
          icon: 'error',
          text: `Unable to generate PDF: ${errorMessage}`,
        })
        return
      }

      if (!hasValidPageSet) {
        const errorMessage = 'PageSet not loaded yet for PDF generation'
        console.error(errorMessage, { pageSet })
        cleanupPdfGeneration()
        setToast({
          icon: 'error',
          text: `Unable to generate PDF: ${errorMessage}`,
        })
        return
      }

      try {
        const body = {
          html: isProof
            ? PDF_PROCESSOR_TYPE.pdfProofProduct
            : PDF_PROCESSOR_TYPE.pdfProduct,
          pageHeight: selectedLayout.paper_height,
          pageWidth: selectedLayout.paper_width,
          orderId: String(order.id),
          orderPageSetId: String(pageSet?.id),
          productId: String(productInfo.id),
          funeralHomeId: String(order.funeral_home_id),
          applicationType: applicationType as ApplicationType,
          // What this page painted, box by box, for the worker to hold its
          // own paint against; a reading that fails leaves the request as
          // it was, since the render never waits on the check.
          textGeometry: requestTextGeometry(),
        }

        let isPdfComplete = false
        const response = await generatePDFApi(body)
        setGenerationState({
          status: PDF_STATUS_KEY.IN_QUEUE,
          progress: 0,
          pdf: '',
        })

        let proofGalleryMetadata: {
          pdfId: string
          galleryToken: string | null
          screenshotUrls: string[]
        } | null = null

        if (response) {
          if (isProof && !useBannerPdfProof) {
            // Proof flow skips PDF rendering: wait on screenshot job instead.
            proofGalleryMetadata = {
              pdfId: response.id,
              galleryToken: response.galleryToken ?? null,
              screenshotUrls: response.screenshotUrls ?? [],
            }
            setProofGalleryDetails(proofGalleryMetadata)

            let screenshotUrls: string[] = []
            try {
              screenshotUrls = await pollScreenshotStatus(
                proofGalleryMetadata.pdfId,
              )
            } catch (pollError) {
              // Reset proof-specific flags here since the outer catch only
              // handles generic generation state. Then rethrow so the shared
              // logger/sentry/handleError path records the terminal failure.
              setShouldSetProductStatusToEditing(false)
              setShouldSetProductStatusToFinalized(false)
              setShouldSetProductStatusToProofSent(false)
              throw pollError
            }

            const proofReady = screenshotUrls.length > 0
            if (proofReady) {
              setGenerationState({
                status: PDF_STATUS_KEY.COMPLETE,
                progress: 100,
                pdf: '',
              })
              setGenerating(false)
              setShouldSetProductStatusToEditing(false)
              setShouldSetProductStatusToFinalized(false)
              setShouldSetProductStatusToProofSent(true)
            } else {
              // Polling timed out with no screenshots. Reset proof flags and
              // throw so the shared error handler records this as a failure
              // rather than silently leaving the UI in ERROR state.
              setShouldSetProductStatusToEditing(false)
              setShouldSetProductStatusToFinalized(false)
              setShouldSetProductStatusToProofSent(false)
              throw new Error(
                'Screenshot polling completed without any screenshot URLs',
              )
            }
          } else if (useBannerPdfProof) {
            // Banner proof renders as a PDF. Poll PDF status like finalization;
            // checkPDFStatus stores the pdf URL in generation state, and the
            // viewer shows it in an iframe (no screenshot gallery). Product
            // status still moves to "proof sent", not finalized.
            const PDFstatus = await checkPDFStatus(response.id)
            const bannerProofReady =
              'progress' in PDFstatus &&
              PDFstatus.progress === 100 &&
              PDFstatus.status === PDF_STATUS_KEY.COMPLETE &&
              !!PDFstatus.pdf

            if (bannerProofReady) {
              setGenerating(false)
              setShouldSetProductStatusToEditing(false)
              setShouldSetProductStatusToFinalized(false)
              setShouldSetProductStatusToProofSent(true)
            } else {
              setShouldSetProductStatusToEditing(false)
              setShouldSetProductStatusToFinalized(false)
              setShouldSetProductStatusToProofSent(false)
              throw new Error(
                'Banner proof PDF completed without a downloadable URL',
              )
            }
          } else {
            const PDFstatus = await checkPDFStatus(response.id)

            isPdfComplete =
              'progress' in PDFstatus &&
              PDFstatus.progress === 100 &&
              PDFstatus.status === PDF_STATUS_KEY.COMPLETE &&
              !!PDFstatus.pdf

            if (isPdfComplete) {
              setGenerating(false)
              const completeText = t('toast.successfullyFinalized')

              const shouldSetProductStatusToFinalizedImmediately = isFHDirector
              if (shouldSetProductStatusToFinalizedImmediately) {
                setCanCustomize(false)
                setCanRevertFinalization(true)
                setShowPdfViewer(false)
                setShouldSetProductStatusToEditing(false)
                setShouldSetProductStatusToProofSent(false)
                setShouldSetProductStatusToFinalized(false)
                await setProductStatus(STATUS_KEY.FINALIZED)
                setDisableCustomizeProductHeaderDropdowns(false)
                setGeneratingText(
                  t('customizeProductHeader.revertFinalization'),
                )
                setToast({
                  icon: 'check',
                  text: completeText,
                })
              } else {
                setCanCustomize(false)
                setCanRevertFinalization(false)
                setShouldSetProductStatusToEditing(false)
                setShouldSetProductStatusToProofSent(false)
                setShouldSetProductStatusToFinalized(true)
              }
            }
          }
        }
      } catch (error: unknown) {
        const isNetworkError =
          error instanceof TypeError &&
          [
            'Load failed',
            'Failed to fetch',
            'NetworkError when attempting to fetch resource',
          ].includes(error.message)

        const errorMessage = isNetworkError
          ? t('pdf.networkError')
          : error instanceof ScreenshotJobFailedError
            ? t('pdf.screenshotJobFailed')
            : (error as AxiosError<{ message?: string }>)?.response?.data
                ?.message || (error as Error).message
        const msg = ((error as Error).message ?? '').toLowerCase()
        const isTimeout = msg.includes('timeout error navigating to url')
        setGenerationState({
          progress: 0,
          pdf: '',
          status: isTimeout ? PDF_STATUS_KEY.TIMEOUT : PDF_STATUS_KEY.ERROR,
        })
        cleanupPdfGeneration()
        logger.error({
          message: 'error_generating_pdf',
          category: EventCategory.ERROR,
          error: error as AxiosError | Error,
        })
        loggerSentry.error({
          message: 'error_generating_pdf',
          category: EventCategory.ERROR,
          error: error as AxiosError | Error,
        })
        if (isFontLoadFailure(error)) {
          setToast({ icon: 'info', text: t('pdf.fontLoadFailed') })
        } else {
          handleError(errorMessage, error as AxiosError | Error)
        }
      }
    },
    [
      isAdmin,
      t,
      cleanupPdfGeneration,
      setToast,
      order.id,
      order.funeral_home_id,
      pageSet,
      productInfo.id,
      checkPDFStatus,
      pollScreenshotStatus,
      isFHDirector,
      setProductStatus,
      handleError,
      setGenerationState,
      setGenerating,
      setCanRevertFinalization,
      setShowPdfViewer,
      setIsProof,
      setGeneratingText,
      setDisableCustomizeProductHeaderDropdowns,
      setCanCustomize,
      setShouldSetProductStatusToEditing,
      setShouldSetProductStatusToProofSent,
      setShouldSetProductStatusToFinalized,
      setProofGalleryDetails,
      applicationType,
    ],
  )

  return {
    generatePDF,
  }
}
